// PLAN-judgment-cockpit Phase C/D/E/F (2026-05-15)
// PVW Decision Article と H-channel feedback の運用 Lens。
// タブ構成: Overview / Review / Insufficient / Timeline / Reuse / Concern
//
// 依存:
//   - PLAN-article-feedback-layer の各 API (feedback_state filter / reuse_boost / decision_escalation 等)
//   - PLAN-judgment-cockpit Phase 0.5 backend (linked_observations / source_article_id filter)
//
// 設計判断:
//   - queue を意味で分離 (Review: 未評価 / Insufficient: 材料不足 / Concern: 注意ログ)
//   - Reuse Library から `reuse_boost=true` で再検索を発火 (教師信号が UX に到達)
//   - 各 row に inline JudgmentFeedbackActions、書き込み後は row 再 fetch

import { useCallback, useEffect, useMemo, useState } from 'react'
import { searchArticles, type ArticleFilters } from '../../api/kdb'
import { apiFetch } from '../../api/client'
import { useSelectedItem } from '../hooks/useSelectedItem'
import { useV2Router } from '../router'
import type { SearchHit } from '../../types'
import { JudgmentFeedbackActions } from '../components/JudgmentFeedbackActions'

type Tab = 'overview' | 'review' | 'insufficient' | 'timeline' | 'reuse' | 'concern' | 'quality'

const TABS: { id: Tab; label: string; icon: string; hint: string }[] = [
  { id: 'overview',     label: 'Overview',     icon: '📊', hint: '判断運用指標サマリ' },
  { id: 'review',       label: 'Review Queue', icon: '👀', hint: '未評価の判断 Article (lineage latest + version feedback 0)' },
  { id: 'insufficient', label: 'Insufficient', icon: '⛔', hint: 'x_status=all_rejected — 材料不足、評価でなく再実行待ち' },
  { id: 'quality',      label: 'Quality',      icon: '🔧', hint: '状態検出: weak_context Article / review_by 超過' },
  { id: 'timeline',     label: 'Timeline',     icon: '📅', hint: '全 Decision Article 時系列' },
  { id: 'reuse',        label: 'Reuse Library', icon: '♻️', hint: 'reuse_ok lineage、search の reuse_boost 起点' },
  { id: 'concern',      label: 'Concern Log',  icon: '⚠️', hint: 'concern / dislike / reuse_ng feedback 付き Article' },
]

// =====
// Main Lens
// =====
export function JudgmentLens() {
  const [tab, setTab] = useState<Tab>('review')

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-6 py-4 border-b border-border-subtle">
        <h1 className="text-xl font-semibold text-text-base mb-1">⚖️ Judgment</h1>
        <p className="text-xs text-text-subtle">
          PVW Decision Article の運用 Lens。判断後評価 (H-channel) と事後観察 (O-channel) を監督します。
        </p>
      </header>
      <nav role="tablist" className="flex flex-wrap items-center gap-1 px-6 py-2 border-b border-border-subtle bg-bg-base">
        {TABS.map((t) => {
          const active = tab === t.id
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              title={t.hint}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded text-sm transition ${
                active
                  ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/40'
                  : 'text-text-subtle hover:text-text-base hover:bg-bg-overlay border border-transparent'
              }`}
            >
              <span aria-hidden className="mr-1">{t.icon}</span>{t.label}
            </button>
          )
        })}
      </nav>
      <div className="flex-1 overflow-auto px-6 py-4">
        {tab === 'overview'     && <OverviewTab onNavigate={setTab} />}
        {tab === 'review'       && <ReviewQueueTab />}
        {tab === 'insufficient' && <InsufficientQueueTab />}
        {tab === 'quality'      && <QualityQueueTab />}
        {tab === 'timeline'     && <TimelineTab />}
        {tab === 'reuse'        && <ReuseLibraryTab />}
        {tab === 'concern'      && <ConcernLogTab />}
      </div>
    </div>
  )
}

export function JudgmentSidebar() {
  // 当面 sub-sidebar なし (将来 domain filter 等を追加する場所として確保)
  return null
}

// =====
// 共有 helper
// =====
function useArticleSearch(filters: ArticleFilters, key: string, sort: '-created_at' | 'created_at' | 'score' = '-created_at') {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hits, setHits] = useState<SearchHit[]>([])
  const [total, setTotal] = useState(0)
  const [reloadAt, setReloadAt] = useState(0)
  const reload = useCallback(() => setReloadAt(Date.now()), [])

  useEffect(() => {
    const ctrl = new AbortController()
    setLoading(true)
    setError(null)
    searchArticles(filters, 50, 0, ctrl.signal, { sort })
      .then((res) => {
        setHits(res.entries)
        setTotal(res.total)
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return
        setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => setLoading(false))
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, reloadAt, sort])
  return { loading, error, hits, total, reload }
}

// =====
// Row component
// =====
interface JudgmentRowProps {
  hit: SearchHit & { decision_hash?: string | null }
  showFeedback?: boolean
  onActionDone?: () => void
  extraBadges?: React.ReactNode
  /** Concern Log の lifecycle_stage 切替 action を出すかどうか */
  showDeprecateAction?: boolean
}

function JudgmentRow({ hit, showFeedback = true, onActionDone, extraBadges, showDeprecateAction }: JudgmentRowProps) {
  const { setSelectedItem } = useSelectedItem()
  const [deprecating, setDeprecating] = useState(false)

  const itemType: 'entry' | 'article' = hit.origin === 'synthesized' ? 'article' : 'entry'

  // PVW projection は SearchHit に top-level で来る可能性があるが、decision_hash は search レスポンスに含めていない
  // → version scope feedback 時に必要。Article GET で取得する必要がある。row では取得していないので、
  //   button から取得する経路を追加するか、SearchHit に projection されているかをチェックする。
  //   ここでは search レスポンスの未提供を許容し、version button は disabled に倒す (lineage scope はそのまま使える)
  const decisionHash = (hit as { decision_hash?: string }).decision_hash ?? null

  const handleDeprecate = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm(`"${hit.title}" を deprecated にしますか?`)) return
    setDeprecating(true)
    try {
      // Article PATCH は article worker proxy。Cockpit から /api/articles/:id PATCH を呼ぶ。
      await apiFetch(`/api/articles/${encodeURIComponent(hit.id)}`, {
        method: 'PATCH',
        body: { article_meta: { lifecycle_stage: 'deprecated' } },
      })
      onActionDone?.()
    } catch (err: unknown) {
      alert(`deprecate 失敗: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setDeprecating(false)
    }
  }

  return (
    <div
      role="row"
      onClick={() => setSelectedItem({ id: hit.id, itemType })}
      className="border border-border-subtle rounded-md p-3 mb-2 bg-bg-surface hover:bg-bg-overlay cursor-pointer transition"
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-base font-medium text-text-base truncate">{hit.title}</span>
            {hit.x_status && <Badge tone={hit.x_status === 'all_rejected' ? 'warn' : 'neutral'}>{`x: ${hit.x_status}`}</Badge>}
            {hit.decision_confidence && <Badge tone={hit.decision_confidence === 'low' ? 'warn' : 'neutral'}>{`conf: ${hit.decision_confidence}`}</Badge>}
            {hit.decision_escalation === 1 && <Badge tone="warn">escalation</Badge>}
            {hit.applicability === 'reusable' && <Badge tone="ok">reusable</Badge>}
            {hit.applicability === 'avoid' && <Badge tone="warn">avoid</Badge>}
            {hit.lifecycle_stage && hit.lifecycle_stage !== 'active' && <Badge tone="muted">{hit.lifecycle_stage}</Badge>}
            {extraBadges}
          </div>
          {hit.one_line && <p className="text-xs text-text-subtle truncate">{hit.one_line}</p>}
          <p className="text-[11px] text-text-subtle mt-1">
            {new Date(hit.created_at).toISOString().slice(0, 10)}
            {hit.source_project && <span> · {hit.source_project}</span>}
            {hit.sourced_count !== undefined && hit.sourced_count > 0 && <span> · sourced {hit.sourced_count}</span>}
          </p>
        </div>
      </div>
      {(showFeedback || showDeprecateAction) && (
        <div className="mt-2 flex items-center gap-3 flex-wrap" onClick={(e) => e.stopPropagation()}>
          {showFeedback && (
            <JudgmentFeedbackActions
              articleId={hit.id}
              decisionHash={decisionHash}
              onWrite={onActionDone}
              compact
            />
          )}
          {showDeprecateAction && (
            <button
              type="button"
              onClick={handleDeprecate}
              disabled={deprecating}
              className="text-xs px-2 py-1 rounded border border-state-warning/40 text-state-warning hover:bg-state-warning/10 disabled:opacity-40 transition"
            >
              {deprecating ? '…' : 'deprecate'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function Badge({ children, tone }: { children: React.ReactNode; tone: 'neutral' | 'warn' | 'ok' | 'muted' }) {
  const cls =
    tone === 'warn'   ? 'bg-state-warning/15 text-state-warning border-state-warning/40' :
    tone === 'ok'     ? 'bg-state-success/15 text-state-success border-state-success/40' :
    tone === 'muted'  ? 'bg-bg-overlay text-text-subtle border-border-subtle' :
                        'bg-bg-overlay text-text-base border-border-subtle'
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>{children}</span>
}

function Status({ loading, error, empty, children }: { loading: boolean; error: string | null; empty: boolean; children: React.ReactNode }) {
  if (loading) return <p className="text-sm text-text-subtle">loading…</p>
  if (error) return <p className="text-sm text-state-warning">{error}</p>
  if (empty) return <p className="text-sm text-text-subtle">該当する Article はありません</p>
  return <>{children}</>
}

// =====
// Overview
// =====
function OverviewTab({ onNavigate }: { onNavigate: (t: Tab) => void }) {
  const [counts, setCounts] = useState<{ decision: number; unreviewed: number; insufficient: number; concern: number; reuse_ok: number; escalation: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    Promise.all([
      searchArticles({ has_decision: true }, 1, 0, ctrl.signal),
      searchArticles({ has_decision: true, feedback_state: 'unreviewed' }, 1, 0, ctrl.signal),
      searchArticles({ has_decision: true, x_status: 'all_rejected' }, 1, 0, ctrl.signal),
      searchArticles({ has_decision: true, feedback_state: 'concern' }, 1, 0, ctrl.signal),
      searchArticles({ has_decision: true, feedback_state: 'reuse_ok' }, 1, 0, ctrl.signal),
      searchArticles({ has_decision: true, decision_escalation: true }, 1, 0, ctrl.signal),
    ])
      .then(([dec, un, ins, con, ro, esc]) => setCounts({
        decision: dec.total, unreviewed: un.total, insufficient: ins.total,
        concern: con.total, reuse_ok: ro.total, escalation: esc.total,
      }))
      .catch((e) => { if (e?.name !== 'AbortError') setError(e instanceof Error ? e.message : String(e)) })
    return () => ctrl.abort()
  }, [])

  if (error) return <p className="text-sm text-state-warning">{error}</p>
  if (!counts) return <p className="text-sm text-text-subtle">loading…</p>

  const cards: { k: keyof typeof counts; label: string; tone: 'neutral' | 'warn' | 'ok'; nav?: Tab }[] = [
    { k: 'decision', label: 'Decision Articles total', tone: 'neutral' },
    { k: 'unreviewed', label: 'Unreviewed (Review Queue)', tone: 'warn', nav: 'review' },
    { k: 'insufficient', label: 'Insufficient material (all_rejected)', tone: 'warn', nav: 'insufficient' },
    { k: 'escalation', label: 'Escalation needed (decision_trace)', tone: 'warn', nav: 'review' },
    { k: 'concern', label: 'Concern / dislike feedback あり', tone: 'warn', nav: 'concern' },
    { k: 'reuse_ok', label: 'Reuse OK lineages', tone: 'ok', nav: 'reuse' },
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {cards.map((c) => (
        <button
          key={c.k}
          type="button"
          onClick={() => c.nav && onNavigate(c.nav)}
          className={`text-left p-4 rounded border bg-bg-surface hover:bg-bg-overlay transition ${
            c.tone === 'warn' ? 'border-state-warning/30' :
            c.tone === 'ok'   ? 'border-state-success/30' :
                                'border-border-subtle'
          }`}
        >
          <div className="text-3xl font-semibold text-text-base">{counts[c.k]}</div>
          <div className="text-xs text-text-subtle mt-1">{c.label}</div>
        </button>
      ))}
    </div>
  )
}

// =====
// Review Queue
// =====
function ReviewQueueTab() {
  const filters = useMemo<ArticleFilters>(() => ({
    has_decision: true,
    feedback_state: 'unreviewed',
  }), [])
  const { loading, error, hits, total, reload } = useArticleSearch(filters, 'review')

  return (
    <section>
      <header className="mb-3">
        <h2 className="text-sm font-medium text-text-base">未評価の判断 Article ({total})</h2>
        <p className="text-xs text-text-subtle mt-1">
          lineage latest 版に version-scope feedback が 0 件、かつ `x_status != all_rejected`。<br/>
          1 件でも version scope feedback を付ければ queue から消えます。新版発行で再点灯します。
        </p>
      </header>
      <Status loading={loading} error={error} empty={hits.length === 0}>
        {hits.map((h) => <JudgmentRow key={h.id} hit={h} onActionDone={reload} />)}
      </Status>
    </section>
  )
}

// =====
// Insufficient Material Queue (x_status=all_rejected を Review Queue から分離)
// =====
function InsufficientQueueTab() {
  const filters = useMemo<ArticleFilters>(() => ({
    has_decision: true,
    x_status: 'all_rejected',
  }), [])
  const { loading, error, hits, total, reload } = useArticleSearch(filters, 'insufficient')

  return (
    <section>
      <header className="mb-3">
        <h2 className="text-sm font-medium text-text-base">材料不足の Article ({total})</h2>
        <p className="text-xs text-text-subtle mt-1">
          PVW-DSL 上 `x_status=all_rejected` は「材料不足で人に戻す」状態。評価対象ではなく**再実行 / 人間判断材料投入待ち**です。
          concern / comment は version-scope feedback で残せます。
        </p>
      </header>
      <Status loading={loading} error={error} empty={hits.length === 0}>
        {hits.map((h) => <JudgmentRow key={h.id} hit={h} onActionDone={reload} />)}
      </Status>
    </section>
  )
}

// =====
// Decision Timeline
// =====
function TimelineTab() {
  const [filterEscalation, setFilterEscalation] = useState(false)
  const [filterLowConfidence, setFilterLowConfidence] = useState(false)
  const [sort, setSort] = useState<'-created_at' | 'created_at'>('-created_at')

  const filters = useMemo<ArticleFilters>(() => ({
    has_decision: true,
    ...(filterEscalation ? { decision_escalation: true } : {}),
    ...(filterLowConfidence ? { decision_confidence: 'low' as const } : {}),
  }), [filterEscalation, filterLowConfidence])

  const key = `timeline:${filterEscalation}:${filterLowConfidence}`
  const { loading, error, hits, total, reload } = useArticleSearch(filters, key, sort)

  return (
    <section>
      <header className="mb-3 flex items-center gap-3 flex-wrap">
        <h2 className="text-sm font-medium text-text-base">Decision Timeline ({total})</h2>
        <label className="text-xs flex items-center gap-1 text-text-subtle">
          Sort:
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as '-created_at' | 'created_at')}
            className="bg-bg-surface border border-border-subtle rounded px-1.5 py-0.5"
          >
            <option value="-created_at">新しい順 (default)</option>
            <option value="created_at">古い順</option>
          </select>
        </label>
        <label className="text-xs flex items-center gap-1 text-text-subtle">
          <input type="checkbox" checked={filterEscalation} onChange={(e) => setFilterEscalation(e.target.checked)} />
          escalation のみ
        </label>
        <label className="text-xs flex items-center gap-1 text-text-subtle">
          <input type="checkbox" checked={filterLowConfidence} onChange={(e) => setFilterLowConfidence(e.target.checked)} />
          low confidence のみ
        </label>
      </header>
      <Status loading={loading} error={error} empty={hits.length === 0}>
        {hits.map((h) => <JudgmentRow key={h.id} hit={h} onActionDone={reload} />)}
      </Status>
    </section>
  )
}

// =====
// Reuse Library — applicability='reusable' な lineage 一覧 + reuse_boost 再検索導線
// =====
function ReuseLibraryTab() {
  const filters = useMemo<ArticleFilters>(() => ({
    has_decision: true,
    feedback_state: 'reuse_ok',
  }), [])
  const { loading, error, hits, total, reload } = useArticleSearch(filters, 'reuse')
  const { navigate } = useV2Router()

  const reuseBoostSearch = () => {
    // Explore Lens に遷移 + reuse_boost=true 状態で再検索
    // (Explore は独自 filter を持つので、ここでは URL に hint を載せて navigate するに留める)
    try {
      sessionStorage.setItem('judgment.reuse_boost', 'true')
    } catch {}
    navigate('explore')
  }

  return (
    <section>
      <header className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-medium text-text-base">Reuse Library ({total})</h2>
          <p className="text-xs text-text-subtle mt-1">
            lineage scope で `reuse_ok` が付いた高品質 lineage の一覧。`applicability='reusable'` 派生。
          </p>
        </div>
        <button
          type="button"
          onClick={reuseBoostSearch}
          title="Explore Lens で reuse_boost=true 検索を有効化"
          className="text-xs px-3 py-1.5 rounded border border-accent-primary/40 text-accent-primary hover:bg-accent-primary/10 transition"
        >
          🔍 Explore で reuse_boost 検索を開始
        </button>
      </header>
      <Status loading={loading} error={error} empty={hits.length === 0}>
        {hits.map((h) => (
          <JudgmentRow
            key={h.id} hit={h} onActionDone={reload}
            extraBadges={<Badge tone="ok">reuse_ok</Badge>}
          />
        ))}
      </Status>
    </section>
  )
}

// =====
// Quality Queue — 状態検出 (PLAN-judgment-cockpit Phase 2)
// weak_context Article + review_by 超過 Article。feedback の有無に依らず「手を入れるべき」signal。
// =====
function QualityQueueTab() {
  const [signal, setSignal] = useState<'weak_context' | 'review_overdue'>('weak_context')

  const filters = useMemo<ArticleFilters>(() => {
    if (signal === 'weak_context') return { text_schema_status: 'weak_context' }
    return { review_overdue: true }
  }, [signal])

  const { loading, error, hits, total, reload } = useArticleSearch(filters, `quality:${signal}`)

  return (
    <section>
      <header className="mb-3 flex items-center gap-3 flex-wrap">
        <h2 className="text-sm font-medium text-text-base">Quality Queue ({total})</h2>
        <label className="text-xs flex items-center gap-1 text-text-subtle">
          検出 signal:
          <select
            value={signal}
            onChange={(e) => setSignal(e.target.value as 'weak_context' | 'review_overdue')}
            className="bg-bg-surface border border-border-subtle rounded px-1.5 py-0.5"
          >
            <option value="weak_context">text_schema_status = weak_context</option>
            <option value="review_overdue">review_by 超過</option>
          </select>
        </label>
      </header>
      <p className="text-xs text-text-subtle mb-3">
        判断とは独立に「品質を上げるために手を入れるべき」状態を検出する queue。Review Queue (feedback 待ち) とは別の用途。
      </p>
      <Status loading={loading} error={error} empty={hits.length === 0}>
        {hits.map((h) => <JudgmentRow key={h.id} hit={h} onActionDone={reload} />)}
      </Status>
    </section>
  )
}

// =====
// Concern Log — concern / dislike / reuse_ng + lifecycle_stage 切替 action
// =====
function ConcernLogTab() {
  const filters = useMemo<ArticleFilters>(() => ({
    has_decision: true,
    feedback_state: 'concern',
  }), [])
  const { loading, error, hits, total, reload } = useArticleSearch(filters, 'concern')

  return (
    <section>
      <header className="mb-3">
        <h2 className="text-sm font-medium text-text-base">Concern Log ({total})</h2>
        <p className="text-xs text-text-subtle mt-1">
          注意・違和感を貼った Article。inline で `deprecated` への lifecycle_stage 切替が可能。
        </p>
      </header>
      <Status loading={loading} error={error} empty={hits.length === 0}>
        {hits.map((h) => (
          <JudgmentRow
            key={h.id}
            hit={h}
            onActionDone={reload}
            showDeprecateAction
            extraBadges={<Badge tone="warn">concern</Badge>}
          />
        ))}
      </Status>
    </section>
  )
}
